import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getCitizenById } from "../redux/reducer/citizensReducer";
import { useAppDispatch } from "../redux";
import { getSurveyList } from "../redux/reducer/surveyReducer";
import { getSurveyHistory } from "../redux/reducer/surveyHistoryReducer";
import { getErrorMessage } from "../utils/helpers";
import Header from "../components/header";
import ButtonSubmit from "../components/common/buttonSubmit";
import ButtonHeader from "../components/common/buttonHeader";
import SurveyListTable from "../components/survey/surveyListTable";
import SurveySearchBox from "../components/survey/surveyFilterBox";
import SuccessAlertMessage from "../components/alertMessageModal/SuccessAlertMessage";
import ErrorAlertMessage from "../components/alertMessageModal/ErrorAlertMessage";
import SurveyHistorySearchBox from "../components/survey/surveyHistory/surveyHistorySearchBox";
import SurveyHistory from "../components/survey/surveyHistory";

// Component starts
const CitizenDetail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  /**
   * Get url params.
   */
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const notification = searchParams.get("notification");
  const type = searchParams.get("type");

  const [notificationType, setNotificationType] = useState(notification);

  /**
   * redux hooks.
   */
  const { namePharmacy, user, eventHash } = useSelector(
    (state: any) => state.login
  );
  const { citizens } = useSelector((state: any) => state);
  const { editCitizen, apiError } = citizens;

  useEffect(() => {
    if (id) {
      fetchData(id);
    }
    if (notification) {
      setNotificationType(notification);
    }
  }, [id, notification]);

  /**
   * fetch citizen and surverys data on page load.
   */
  const fetchData = async (id: any) => {
    dispatch(getSurveyList());
    dispatch(getCitizenById(id));
    dispatch(getSurveyHistory(id));
  };

  /**
   * update notificationType value
   */
  const handleClose = () => {
    setNotificationType("");
  };

  /**
   * Get success text messages.
   */
  const getSuccessMessage = (type: any) => {
    switch (type) {
      case "survey":
        return t("add-survey-success");
      case "deleteSurvey":
        return t("survey-delete");
      default:
        return t("update-citizen-success");
    }
  };

  return (
    <>
      {notificationType === "success" && (
        <SuccessAlertMessage
          icon="info"
          handleClose={handleClose}
          message={getSuccessMessage(type)}
        />
      )}

      {notificationType === "error" && (
        <ErrorAlertMessage
          icon="info"
          handleClose={handleClose}
          message={
            apiError?.key
              ? getErrorMessage(apiError)
              : t("update-citizen-error")
          }
        />
      )}

      <Header active={true} titleLogOut={user?.username} />
      {/* render back button and page headline title */}
      <div className="container-box d-flex align-items-center col-lg-12 mb-0">
        <div className="col-lg-3">
          <div className="d-inline">
            <ButtonHeader
              className="btn btnHeader d-flex align-items-center"
              handleChange={() => navigate("/enrollment")}
              icon="chevron_left"
              textButton={t("go-back")}
            />
          </div>
        </div>
        <div className="page-title col-lg-6">
          <h1 className="mb-2">{namePharmacy}</h1>
          <div className="d-flex justify-content-center">
            {eventHash && (
              <p>
                <b>{t("regional-code")}:</b> {eventHash}
              </p>
            )}
          </div>
        </div>
        <div className="col-lg-3"></div>
      </div>
      {/* display citizen details - header row section */}
      <div className="container-box mb-3">
        <div className="survey-wrapper-box info-content">
          <div className="d-flex align-items-center justify-content-between">
            <span className="material-icons ms-2 tick-icon-color">check</span>
            {t("citizen-detail")}
            <span className="fw-bold">&nbsp;{editCitizen?.name}&nbsp;</span>
            <span className="fw-bold">{editCitizen?.surname}&nbsp;</span>
            {t("with-fiscal-code")}
            <span className="fw-bold">
              &nbsp;{editCitizen?.crm?.extraFields?.fiscalcode?.value}
            </span>
          </div>
          <ButtonSubmit
            spinner={false}
            textButton={t("modify-citizen")}
            handleChange={() =>
              navigate(`/citizen-form/${id}`, {
                state: { urlHistory: `/enrollment/citizen-detail/${id}` },
              })
            }
          />
        </div>
      </div>

      {/* Render surveys list with filter box */}
      <SurveySearchBox />
      <SurveyListTable />

      {/* Render surveys history */}
      <SurveyHistorySearchBox />
      <SurveyHistory />
    </>
  );
};

export default CitizenDetail;
